import {
    OrigemFaltaCadastrada,
    TRFaltaInjustificada,
    TRFaltaJustificada,
} from '@acme/core'
import dayjs from 'dayjs'
import Dialog from 'global/guides/Dialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { Info, Sparkles } from 'lucide-react'
import { useAusenciasOutletContext } from 'pages/Ausencias.context'
import { SubmitHandler } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import {
    FaltaCadastrada,
    faltasCadastradas,
} from 'service/api/endpoints/faltas-cadastradas'
import { toast } from 'sonner'
import { useStore } from 'store'
import FormEditarFaltaCadastrada from 'templates/ManutencaoDoPonto/Digitacao/forms/EditarFaltaCadastrada'
import IconeCadeado from './IconeCadeado'
import invalidateAusenciasQuery from './invalidateAusenciasQuery'

export default function EditarFaltaCadastrada() {
    const { id } = useParams()
    const { data, competencia } = useAusenciasOutletContext()
    const { mutateAsync } = faltasCadastradas.mutations.useUpdate()
    const empresa = useStore(state => state.company)
    const isEdicaoBloqueada = empresa?.competenciaAtual !== competencia
    const row = data?.find(a =>
        ['falta-injustificada_' + id, 'falta-justificada_' + id].includes(a.id),
    ) as TRFaltaInjustificada | TRFaltaJustificada
    if (!row || !empresa) return null

    const handleSubmit: SubmitHandler<
        FaltaCadastrada & { isInjustificada?: boolean }
    > = data => {
        if (data.isInjustificada) data.justificativa = ''
        return mutateAsync(
            {
                ...data,
                id: Number(String(row.id).split('_').at(-1)),
                data: dayjs(data.data).toDate(),
            },
            {
                onSuccess: async () => {
                    await invalidateAusenciasQuery()
                    await faltasCadastradas.invalidateQueries()
                    toast.success('Falta atualizada com sucesso')
                },
                onError: err => {
                    toast.error(err.message)
                },
            },
        )
    }

    return (
        <Dialog.Root
            defaultValues={{ ...row, isInjustificada: !row.justificativa }}
            onSubmit={isEdicaoBloqueada ? undefined : handleSubmit}
            disableFields={isEdicaoBloqueada}
            open={hasPermission([
                'update',
                'FaltasJustificadasOuInjustificadas',
            ])}
        >
            <Dialog.Title
                action={`Editar falta ${row.justificativa ? 'justificada' : 'injustificada'}`}
                endAdornment={
                    <div className="flex items-center gap-2">
                        {row.origem === OrigemFaltaCadastrada.AUTOMATICA && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2 py-1 text-xs font-medium"
                                title="Criada automaticamente pelo sistema durante o cálculo"
                            >
                                <Sparkles className="h-3 w-3" />
                                Automática
                            </span>
                        )}
                        {row.origem === OrigemFaltaCadastrada.MANUAL && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-2 py-1 text-xs font-medium"
                                title="Criada ou editada manualmente - não será alterada pelo cálculo automático"
                            >
                                <Info className="h-3 w-3" />
                                Manual
                            </span>
                        )}
                    </div>
                }
            >
                {row.funcionarioNome} · {row.dataInicio}{' '}
                {isEdicaoBloqueada && <IconeCadeado />}
            </Dialog.Title>
            <Dialog.Content>
                <FormEditarFaltaCadastrada />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton closeOnSubmit />
            </Dialog.Actions>
        </Dialog.Root>
    )
}
